import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import useSWR from 'swr'
import { createStripeCheckoutSession, getStripePrices } from '../../api'
import { useAlertFromSearchParams } from '../../hooks/useAlertFromSearchParams'
import Button, { ButtonVariant } from '../../components/ui/button'
import './styles.scss'
import TinyInvoiceLogo from '../../components/ui/logo'

type PriceInfo = { priceId: string; unitAmount: number; currency: string; interval: string } | null

const formatPrice = (price: PriceInfo, locale: string): string => {
  if (!price || price.unitAmount == null) return ''
  const value = price.unitAmount / 100
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: price.currency.toUpperCase(),
  }).format(value)
}

/** Savings when choosing yearly vs 12× monthly (0–100). */
const getYearlySavingsPercent = (monthly: PriceInfo, yearly: PriceInfo): number | null => {
  if (!monthly?.unitAmount || !yearly?.unitAmount || monthly.unitAmount <= 0) return null
  const costIfMonthly12 = 12 * monthly.unitAmount
  const costYearly = yearly.unitAmount
  if (costYearly >= costIfMonthly12) return null
  return Math.round(((costIfMonthly12 - costYearly) / costIfMonthly12) * 100)
}

const GetPremiumPage = () => {
  useAlertFromSearchParams()
  const navigate = useNavigate()
  const [loading, setLoading] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const locale = typeof navigator !== 'undefined' ? navigator.language : 'en'
  const { data: prices } = useSWR('/api/stripe/prices', getStripePrices)
  const monthlyFormatted = formatPrice(prices?.monthly ?? null, locale)
  const yearlyFormatted = formatPrice(prices?.yearly ?? null, locale)
  const monthlyPriceId = prices?.monthly?.priceId
  const yearlyPriceId = prices?.yearly?.priceId
  const yearlySavingsPercent = getYearlySavingsPercent(prices?.monthly ?? null, prices?.yearly ?? null)

  const handleSelectPlan = async (priceId: string, label: string) => {
    if (!priceId) {
      setError('This plan is not configured. Please set Stripe Price IDs in your environment.')
      return
    }
    setError(null)
    setLoading(label)
    try {
      const res = await createStripeCheckoutSession(priceId)
      if (res?.url) {
        window.location.href = res.url
      } else {
        setError('Something went wrong. Please try again.')
        setLoading(null)
      }
    } catch (e: any) {
      setError(e?.message || 'Something went wrong. Please try again.')
      setLoading(null)
    }
  }

  return (
    <div className='get-premium-page'>
      <div className='get-premium-container'>
        <div className='text-center mb-4'><TinyInvoiceLogo width={250} /></div>
        <div className='get-premium-header'>
          <h1>Get Premium</h1>
          <p className='lead'>
            Send unlimited invoices.
          </p>
        </div>

        <div className='get-premium-cards'>
          <div className='premium-card'>
            <h2>Monthly</h2>
            {monthlyFormatted && (
              <p className='premium-price'>{monthlyFormatted}<span className='premium-interval'>/month</span></p>
            )}
            <p className='premium-desc'>Cancel anytime.</p>
            <Button
              variant={ButtonVariant.SECONDARY}
              disabled={!!loading || !monthlyPriceId}
              onClick={() => handleSelectPlan(monthlyPriceId ?? '', 'monthly')}
            >
              {loading === 'monthly' ? 'Taking you to checkout…' : 'Choose monthly'}
            </Button>
          </div>
          <div className='premium-card premium-card-featured'>
            {!yearlySavingsPercent && <span className='badge'>Best value</span>}
            {yearlySavingsPercent != null && yearlySavingsPercent > 0 && (
              <span className='badge badge-savings'>Save {yearlySavingsPercent}%</span>
            )}
            <h2>Yearly</h2>
            {yearlyFormatted && (
              <p className='premium-price'>{yearlyFormatted}<span className='premium-interval'>/year</span></p>
            )}
            <p className='premium-desc'>Cancel anytime.</p>
            <Button
              variant={ButtonVariant.PRIMARY}
              disabled={!!loading || !yearlyPriceId}
              onClick={() => handleSelectPlan(yearlyPriceId ?? '', 'yearly')}
            >
              {loading === 'yearly' ? 'Taking you to checkout…' : 'Choose yearly'}
            </Button>
          </div>
        </div>

        {error && (
          <div className='alert alert-danger mt-3' role='alert'>
            {error}
          </div>
        )}

        <p className='get-premium-footer'>
          Secure payment by Stripe. You can update your card or cancel your subscription anytime from your account.
        </p>

        <button type='button' className='btn btn-link back-link' onClick={() => navigate('/dashboard')}>
          ← Back to my invoices
        </button>
      </div>
    </div>
  )
}

export default GetPremiumPage
